import Vue from 'vue';


export const isURL = (text = '') => /^((https?:\/\/|www)[^\s]+)/g.test(text.toLowerCase());


// https://stackoverflow.com/questions/400212/how-do-i-copy-to-the-clipboard-in-javascript?answertab=votes#tab-top
// and
// https://josephkhan.me/javascript-copy-clipboard-safari/#:~:text=A%20working%20example%20of%20copy%20to%20clipboard%20using%20JavaScript
export const copyTextToClipboard = (text) => {
  if (navigator.clipboard) {
    return navigator.clipboard.writeText(text);
  }

  return new Promise((resolve, reject) => {
    const textArea = document.createElement('textarea');
    textArea.readOnly = true;
    textArea.contentEditable = true;
    textArea.value = text;
    document.body.appendChild(textArea);

    if (navigator.userAgent.match(/ipad|iphone/i)) {
      const range = document.createRange();
      range.selectNodeContents(textArea);

      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      textArea.setSelectionRange(0, 2);

    } else {
      textArea.focus();
      textArea.select();
    }

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        resolve();
      } else {
        reject(new Error('Unsuccessful'));
      }
    } catch (err) {
      reject(err);
    }

    document.body.removeChild(textArea);
  });
};


export const isTouchDevice = (() => {
  const prefixes = ' -webkit- -moz- -o- -ms- '.split(' ');

  const mq = (query) => {
    return window.matchMedia(query).matches;
  };

  if ('ontouchstart' in window || window.DocumentTouch && document instanceof window.DocumentTouch) {
    return true;
  }

  // include the 'heartz' as a way to have a non matching MQ to help terminate the join
  // https://git.io/vznFH
  const query = ['(', prefixes.join('touch-enabled),('), 'heartz', ')'].join('');
  return mq(query);
})();


export const canInviteUsers = (room) => {
  if (room.id !== 'local') {
    if (room.private && room.owner.isCurrentUser) {
      return true;
    } else if (!room.private) {
      return true;
    }
  }
  return false;
};


// https://stackoverflow.com/a/14919494
/**
 * Format bytes as human-readable text.
 *
 * @param {number} bytes Number of bytes.
 * @param {boolean} si True to use metric (SI) units, aka powers of 1000.
 *                     False to use binary (IEC), aka powers of 1024.
 * @param {number} dp Number of decimal places to display.
 * @return {string} Formatted string.
 */
export const formatFileSize = (bytes, si = true, dp = 1) => {
  const thresh = si ? 1000 : 1024;
  if (Math.abs(bytes) < thresh) {
    return bytes + ' B';
  }

  let u = -1;
  const r = 10 ** dp;
  const units = si ?
      ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'] :
      ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];

  do {bytes /= thresh; ++u;}
  while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

  return bytes.toFixed(dp) + ' ' + units[u];
};


export const compareAlphabetically = new Intl.Collator('de').compare;


let hasFocusinListener = [];

export const focusField = (vuetifyField) => {
  const input = vuetifyField.$el.querySelector('input');
  if (!hasFocusinListener.includes(input)) {
    // https://github.com/vuetifyjs/vuetify/issues/6892#issuecomment-478832713
    input.addEventListener('focusin', (e) => e.stopPropagation());
    hasFocusinListener.push(input);
  }
  vuetifyField.$nextTick(() => setTimeout(() => input.focus()));
  return input;
};


export const serializeForm = (form) => {
  const data = {};
  const formData = new FormData(form);
  for (const key of formData.keys()) {
    data[key] = formData.get(key);
  }
  return data;
};


export const participantInitials = (name = '') => {
  const names = name.split(' ').map((n) => n[0]);
  return [names[0], names[names.length - 1]].join('');
};


export const downloadSupported = typeof document.createElement('a').download !== 'undefined';


export const serviceWorkerSupported = 'serviceWorker' in navigator;


export const watchDocumentVisibility = (onChange) => {
  // https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API#Example
  let hidden, visibilityChange;
  if (typeof document.hidden !== 'undefined') { // Opera 12.10 and Firefox 18 and later support
    hidden = 'hidden';
    visibilityChange = 'visibilitychange';
  } else if (typeof document.msHidden !== 'undefined') {
    hidden = 'msHidden';
    visibilityChange = 'msvisibilitychange';
  } else if (typeof document.webkitHidden !== 'undefined') {
    hidden = 'webkitHidden';
    visibilityChange = 'webkitvisibilitychange';
  }

  let visibilityChanged;
  if (hidden !== undefined) {
    visibilityChanged = () => onChange(!document[hidden]);
    document.addEventListener(visibilityChange, visibilityChanged, false);
  }

  const onBlur = () => onChange(false);
  window.addEventListener('blur', onBlur);

  const onFocus = () => onChange(true);
  window.addEventListener('focus', onFocus);

  // Return `unwatch` function.
  return () => {
    if (visibilityChanged) {
      document.removeEventListener(visibilityChange, visibilityChanged);
    }
    window.removeEventListener('blur', onBlur);
    window.removeEventListener('focus', onFocus);
  };
};


export const stringifyError = (error) => {
  if (!(error instanceof Error)) {
    return undefined;
  }
  return {
    ...error,
    message: error.message
  };
};


export const parseError = (errorObj) => {
  if (!errorObj) {
    return null;
  }
  const error = new Error(errorObj.message);
  delete errorObj.message;
  for (const key in errorObj) {
    error[key] = errorObj[key];
  }
  return error;
};


export const routeOptions = (options = {}) => {
  if (options.path?.startsWith('/share') && !options.query?.room) {
    if (!options.query) {
      options.query = {};
    }
    const roomId = Vue.store.getters['share/getRoom']?.id || 'local';
    options.query.room = roomId;
  }
  return options;
};


export const accountUrl = (() => {
  const localDev = ['127.0.0.1', 'localhost'].includes(location.hostname);
  let url = 'accounts.xidras.io/account';
  if (location.hostname.startsWith('staging-') || localDev) {
    url = 'staging-' + url;
  }
  url = 'https://' + url;

  return url;
})();
